import { useEffect, useState, useCallback } from 'react';
import { Activity, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type AuditEntry = {
  id: string;
  tenantId: string;
  tenantName: string | null;
  action: string;
  entityType: string;
  entityId: string;
  userId: string | null;
  operatorId: string | null;
  deviceId: string | null;
  locationId: string | null;
  shiftId: string | null;
  details: any;
  occurredAt: string;
};

type Tenant = { id: string; name: string };

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  // Filters
  const [tenantFilter, setTenantFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [offset, setOffset] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (tenantFilter) params.set('tenantId', tenantFilter);
      if (entityTypeFilter) params.set('entityType', entityTypeFilter);
      if (actionFilter) params.set('action', actionFilter);
      if (fromDate) params.set('from', new Date(fromDate).toISOString());
      if (toDate) params.set('to', new Date(toDate + 'T23:59:59').toISOString());
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));

      const res = await api.get<{ rows: AuditEntry[]; total: number }>('/admin/audit?' + params.toString());
      setEntries(res.rows);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [tenantFilter, entityTypeFilter, actionFilter, fromDate, toDate, offset]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load filter options once
  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => setTenants(rows.map((r) => ({ id: r.id, name: r.name })))).catch(() => {});
    api.get<string[]>('/admin/audit/entity-types').then(setEntityTypes).catch(() => {});
    api.get<string[]>('/admin/audit/actions').then(setActions).catch(() => {});
  }, []);

  // Reset offset when filters change
  useEffect(() => { setOffset(0); }, [tenantFilter, entityTypeFilter, actionFilter, fromDate, toDate]);

  const formatDate = (iso: string) => new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const clearFilters = () => {
    setTenantFilter('');
    setEntityTypeFilter('');
    setActionFilter('');
    setFromDate('');
    setToDate('');
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Audit log</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Pregled svih sistemskih događaja po tenant-u, entitetu i akciji
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={refresh} disabled={loading} style={btnSecondary}>
          <RefreshCw size={13} /> Osveži
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16, marginBottom: 14, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: 10, alignItems: 'end' }}>
        <Field label="Klijent">
          <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} style={input}>
            <option value="">Svi</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Entitet">
          <select value={entityTypeFilter} onChange={(e) => setEntityTypeFilter(e.target.value)} style={input}>
            <option value="">Svi</option>
            {entityTypes.map((et) => <option key={et} value={et}>{et}</option>)}
          </select>
        </Field>
        <Field label="Akcija">
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={input}>
            <option value="">Sve</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
        <Field label="Od">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={input} />
        </Field>
        <Field label="Do">
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={input} />
        </Field>
        <button onClick={clearFilters} style={{ ...btnSecondary, alignSelf: 'end' }}>Resetuj</button>
      </div>

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Vreme</th>
                <th style={th}>Klijent</th>
                <th style={th}>Akcija</th>
                <th style={th}>Entitet</th>
                <th style={th}>Entity ID</th>
                <th style={th}>User</th>
                <th style={{ ...th, width: 60, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {!loading && entries.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Activity size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema događaja u logu za odabrane filtere</div>
                </td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)' }}>{formatDate(e.occurredAt)}</td>
                  <td style={{ ...td, color: 'var(--color-text-muted)' }}>{e.tenantName || '-'}</td>
                  <td style={{ ...td, color: 'var(--color-text-strong)' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(124,58,237,0.15)', color: '#c4b5fd', fontWeight: 600 }}>
                      {e.action}
                    </span>
                  </td>
                  <td style={{ ...td, color: 'var(--color-text)' }}>{e.entityType}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)' }}>{e.entityId.slice(0, 8)}...</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)' }}>{e.userId?.slice(0, 8) || e.operatorId?.slice(0, 8) || '-'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setSelected(e)} style={iconBtn} title="Detalji"><Eye size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div style={{ padding: '12px 14px', borderTop: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text-muted)' }}>
            <div>
              Prikazano {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} od {total}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                style={{ ...iconBtn, padding: '6px 10px', opacity: offset === 0 ? 0.4 : 1 }}
              >
                <ChevronLeft size={13} />
              </button>
              <span>Strana {currentPage} / {totalPages}</span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                style={{ ...iconBtn, padding: '6px 10px', opacity: offset + PAGE_SIZE >= total ? 0.4 : 1 }}
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && <AuditDetailDialog entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function AuditDetailDialog({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  useEscapeToClose(onClose);
  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...dialog, maxWidth: 640 }}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            Detalji događaja
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflow: 'auto' }}>
          <DetailRow label="ID" value={entry.id} mono />
          <DetailRow label="Vreme" value={new Date(entry.occurredAt).toLocaleString('sr-RS')} />
          <DetailRow label="Klijent" value={entry.tenantName || entry.tenantId} />
          <DetailRow label="Akcija" value={entry.action} />
          <DetailRow label="Entitet" value={`${entry.entityType} / ${entry.entityId}`} mono />
          {entry.userId && <DetailRow label="User ID" value={entry.userId} mono />}
          {entry.operatorId && <DetailRow label="Operator ID" value={entry.operatorId} mono />}
          {entry.deviceId && <DetailRow label="Device ID" value={entry.deviceId} mono />}
          {entry.locationId && <DetailRow label="Location ID" value={entry.locationId} mono />}
          {entry.shiftId && <DetailRow label="Shift ID" value={entry.shiftId} mono />}
          {entry.details && (
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>Details (JSON)</label>
              <pre style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 12, fontSize: 11, color: 'var(--color-text)', overflow: 'auto', margin: 0 }}>
                {JSON.stringify(entry.details, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2, fontWeight: 500 }}>{label}</label>
      <div style={{ fontSize: 13, color: 'var(--color-text)', fontFamily: mono ? 'monospace' : undefined }}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, outline: 'none' };
